/**
 * <faq-search> — recherche prédictive sur les Q/R d'un bloc <tabs-container> sibling.
 *
 * Comportement :
 *  - Au connect, retrouve le <tabs-container> dans le même custom-section parent.
 *  - Indexe TOUS les <details> dans les <template> de chaque tab (contenu caché tant que le tab n'est pas actif).
 *  - À chaque frappe, filtre case-insensitive (question + réponse), priorise les matches dans la question.
 *  - Sur clic d'une suggestion :
 *      1. tab.click() pour activer le bon onglet (le JS natif tabs-container injecte le contenu)
 *      2. requestAnimationFrame × 2 pour attendre l'injection
 *      3. <details>.open = true sur l'accordéon ciblé + scrollIntoView
 */
class FaqSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.faq-search__input');
    this.dropdown = this.querySelector('.faq-search__dropdown');
    this.clearBtn = this.querySelector('.faq-search__clear');

    this.minChars = parseInt(this.dataset.minChars || '2', 10);
    this.maxResults = parseInt(this.dataset.maxResults || '8', 10);
    this.noResultsText = this.dataset.noResults || 'Aucun résultat.';

    // Résolution du <tabs-container> cible — fallback en cascade :
    //  1. Si data-target est défini (ID de section), chercher dans cet élément
    //  2. Sinon, chercher dans le .custom-section parent direct
    //  3. Sinon, prendre le 1er <tabs-container> du document (cas FAQ standard)
    this.tabsContainer = null;
    const targetId = this.dataset.target;
    if (targetId) {
      const targetEl = document.getElementById(targetId);
      if (targetEl) this.tabsContainer = targetEl.querySelector('tabs-container');
    }
    if (!this.tabsContainer) {
      const parentSection = this.closest('.custom-section') || this.closest('section');
      if (parentSection) this.tabsContainer = parentSection.querySelector('tabs-container');
    }
    if (!this.tabsContainer) {
      this.tabsContainer = document.querySelector('tabs-container');
    }

    if (!this.tabsContainer) {
      console.warn('[faq-search] no <tabs-container> found on the page. Search disabled.');
      return;
    }

    // Index : on attend que le tabs-container soit "connected" pour être sûr que les <template> sont là.
    // Mais les <template> sont du HTML pur, donc déjà disponibles dès le parse. On peut indexer direct.
    this.index = this.buildIndex();

    if (!this.index.length) {
      console.warn('[faq-search] no accordions indexed.');
    }

    this.input.addEventListener('input', () => this.handleInput());
    this.input.addEventListener('focus', () => {
      if (this.input.value.trim().length >= this.minChars) this.openDropdown();
    });

    // Clic en dehors → ferme
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.closeDropdown();
    });

    // Escape → ferme + clear focus
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.input.value = '';
        this.closeDropdown();
        this.input.blur();
      }
    });

    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', () => {
        this.input.value = '';
        this.input.focus();
        this.closeDropdown();
        this.updateClearVisibility();
      });
    }
    this.updateClearVisibility();
  }

  buildIndex() {
    const items = [];
    const tabs = this.tabsContainer.querySelectorAll('[data-ref="tab"]');
    tabs.forEach((tab, tabIdx) => {
      const tabName = (tab.querySelector('.tab__button')?.innerText || '').trim();
      const template = tab.querySelector('template');
      if (!template) return;
      const frag = template.content;
      const details = frag.querySelectorAll('details');
      details.forEach((d, accIdx) => {
        // Question : on clone le <summary> et on retire les icônes Material Symbols
        // (sinon innerText capture les noms de ligatures : 'keyboard_arrow_down', 'add', 'remove'…)
        const summary = d.querySelector('summary');
        let q = '';
        if (summary) {
          const sClone = summary.cloneNode(true);
          sClone.querySelectorAll('.icon, .accordion__icons-add, [class*="material-symbols"]').forEach(n => n.remove());
          q = (sClone.textContent || '').replace(/\s+/g, ' ').trim();
        }
        // Réponse : tout le contenu sauf le summary, idem nettoyage des icônes
        const dClone = d.cloneNode(true);
        dClone.querySelector('summary')?.remove();
        dClone.querySelectorAll('.icon, .accordion__icons-add, [class*="material-symbols"]').forEach(n => n.remove());
        const a = (dClone.textContent || '').replace(/\s+/g, ' ').trim();
        if (q) items.push({ q, a, tabIdx, accIdx, tabName });
      });
    });
    return items;
  }

  updateClearVisibility() {
    if (!this.clearBtn) return;
    this.clearBtn.hidden = !this.input.value;
  }

  handleInput() {
    const raw = this.input.value.trim();
    this.updateClearVisibility();
    if (raw.length < this.minChars) {
      this.closeDropdown();
      return;
    }
    const query = raw.toLowerCase();
    const results = this.index
      .map((it) => {
        const qLower = it.q.toLowerCase();
        const aLower = it.a.toLowerCase();
        const qMatch = qLower.includes(query);
        const aMatch = aLower.includes(query);
        if (!qMatch && !aMatch) return null;
        return { ...it, score: qMatch ? 2 : 1 };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score)
      .slice(0, this.maxResults);

    this.renderDropdown(results, query);
  }

  renderDropdown(results, query) {
    if (!results.length) {
      this.dropdown.innerHTML = `<li class="faq-search__no-results">${escapeHtml(this.noResultsText)}</li>`;
    } else {
      this.dropdown.innerHTML = results
        .map(
          (r, i) => `
        <li role="option" data-idx="${i}">
          <button type="button" class="faq-search__suggestion" data-tab-idx="${r.tabIdx}" data-acc-idx="${r.accIdx}">
            <span class="faq-search__suggestion-q">${highlight(r.q, query)}</span>
            <span class="faq-search__suggestion-meta">${escapeHtml(r.tabName)}</span>
          </button>
        </li>`
        )
        .join('');
      this.dropdown.querySelectorAll('.faq-search__suggestion').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.selectResult(parseInt(btn.dataset.tabIdx, 10), parseInt(btn.dataset.accIdx, 10));
        });
      });
    }
    this.openDropdown();
  }

  selectResult(tabIdx, accIdx) {
    const tabs = this.tabsContainer.querySelectorAll('[data-ref="tab"]');
    const target = tabs[tabIdx];
    if (!target) return;

    // 1. Activer le tab cible (le JS natif tabs-container injectera le contenu dans .tabs__panel)
    target.click();

    // 2. Attendre 2 frames pour laisser le JS natif injecter
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const panel = this.tabsContainer.querySelector('[data-ref="tabs-panel"]');
        if (!panel) return;
        const details = panel.querySelectorAll('details');
        const d = details[accIdx];
        if (d) {
          d.open = true;
          d.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Petit flash pour signaler l'item ouvert
          d.classList.add('faq-search-flash');
          setTimeout(() => d.classList.remove('faq-search-flash'), 1400);
        }
        this.closeDropdown();
      })
    );
  }

  positionDropdown() {
    const wrapper = this.querySelector('.faq-search__input-wrapper');
    if (!wrapper) return;
    const rect = wrapper.getBoundingClientRect();
    // position fixed = par rapport au viewport, pas besoin d'ajouter window.scrollY
    this.dropdown.style.top = `${rect.bottom + 8}px`;
    this.dropdown.style.left = `${rect.left}px`;
    this.dropdown.style.width = `${rect.width}px`;
  }

  openDropdown() {
    this.dropdown.hidden = false;
    this.positionDropdown();
    this.setAttribute('data-open', '');
    if (!this._reposition) {
      this._reposition = () => this.positionDropdown();
      window.addEventListener('scroll', this._reposition, { passive: true });
      window.addEventListener('resize', this._reposition);
    }
  }
  closeDropdown() {
    this.dropdown.hidden = true;
    this.removeAttribute('data-open');
    if (this._reposition) {
      window.removeEventListener('scroll', this._reposition);
      window.removeEventListener('resize', this._reposition);
      this._reposition = null;
    }
  }
}

function escapeHtml(s) {
  return String(s).replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c]));
}

function highlight(text, query) {
  const safe = escapeHtml(text);
  const escQ = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (!escQ) return safe;
  return safe.replace(new RegExp(`(${escQ})`, 'gi'), '<mark>$1</mark>');
}

if (!customElements.get('faq-search')) {
  customElements.define('faq-search', FaqSearch);
}
